import { DateTime } from "luxon"

import { getWeekdayHours, type OpeningHour } from "@/utils/opening_hours"

const SIDEBAR_STEP_MINUTES = 15
const SIDEBAR_STEP_SECONDS = SIDEBAR_STEP_MINUTES * 60

export const DEFAULT_SIDEBAR_TABLE_MIN_CAPACITY = 1
export const DEFAULT_SIDEBAR_TABLE_MAX_CAPACITY = 12

type SidebarTable = {
  id: string
  minCapacity?: number | null
  maxCapacity?: number | null
}

export type SidebarTableBusyBlock = {
  tableId: string
  startTime: string
  endTime: string
}

export type SidebarTimeSlot = {
  label: string
  startTime: string
  available: boolean
}

type SidebarOpeningRange = {
  opensAt: DateTime
  closesAt: DateTime
}

type SidebarAvailabilityInput = {
  date: DateTime | null
  openingHours?: OpeningHour[]
  tables?: SidebarTable[]
  busyBlocks?: SidebarTableBusyBlock[]
  timezone: string
  minAdvanceBookingTime?: number | null
  minReservationTime?: number | null
  maxGuestCount?: number
  selectedPeopleAmount?: number
}

const getRoundedBookingThreshold = (value: DateTime) => {
  const totalMinutes = value.minute + value.second / 60 + value.millisecond / 60000
  const roundedMinutes = Math.ceil(totalMinutes / SIDEBAR_STEP_MINUTES) * SIDEBAR_STEP_MINUTES

  return value.startOf("hour").plus({ minutes: roundedMinutes }).set({ second: 0, millisecond: 0 })
}

const getTimeBoundary = (date: DateTime, time: string) => {
  const [hour, minute] = time.split(":").map(Number)
  return date.startOf("day").set({ hour, minute, second: 0, millisecond: 0 })
}

export const getSidebarReservationDuration = (minReservationTime?: number | null) => {
  return Math.max(minReservationTime || 0, SIDEBAR_STEP_SECONDS)
}

export const getSidebarOpeningRange = (
  date: DateTime | null,
  openingHours: OpeningHour[] | undefined,
  timezone: string,
): SidebarOpeningRange | null => {
  if (!date || !openingHours?.length) return null

  const zonedDate = date.setZone(timezone)
  const weekday = zonedDate.weekday % 7
  const hours = getWeekdayHours(openingHours, weekday, zonedDate)

  if (!hours || hours.closed || !hours.startTime || !hours.endTime) {
    return null
  }

  const opensAt = getTimeBoundary(zonedDate, hours.startTime)
  let closesAt = getTimeBoundary(zonedDate, hours.endTime)

  if (closesAt <= opensAt) {
    closesAt = closesAt.plus({ days: 1 })
  }

  return { opensAt, closesAt }
}

export const isSidebarDateClosed = (date: DateTime, openingHours: OpeningHour[] | undefined, timezone: string) => {
  return !getSidebarOpeningRange(date, openingHours, timezone)
}

const buildSidebarTimeSlots = (openingRange: SidebarOpeningRange): DateTime[] => {
  const slots: DateTime[] = []
  const lastSlot = openingRange.closesAt.minus({ minutes: SIDEBAR_STEP_MINUTES })

  if (lastSlot < openingRange.opensAt) return slots

  let current = openingRange.opensAt

  while (current <= lastSlot) {
    slots.push(current)
    current = current.plus({ minutes: SIDEBAR_STEP_MINUTES })
  }

  return slots
}

export const getSidebarOpeningTimeSlots = (
  date: DateTime | null,
  openingHours: OpeningHour[] | undefined,
  timezone: string,
  minAdvanceBookingTime?: number | null,
): SidebarTimeSlot[] => {
  const openingRange = getSidebarOpeningRange(date, openingHours, timezone)
  if (!openingRange) return []

  // Floor today's slots at the rounded `now + minAdvanceBookingTime` threshold (mirrors the desktop
  // sidebar's `bookingThreshold`). This is the min-advance/now gate, NOT table-availability filtering
  // (decision #9 still holds): it only drops slots that have already elapsed / are inside the advance
  // window. On future dates every slot is past the threshold, so nothing is removed.
  const bookingThreshold = minAdvanceBookingTime
    ? getRoundedBookingThreshold(DateTime.now().setZone(timezone).plus({ seconds: minAdvanceBookingTime }))
    : DateTime.now().setZone(timezone)

  return buildSidebarTimeSlots(openingRange)
    .filter((slot) => slot >= bookingThreshold)
    .map((slot) => ({
      label: slot.toFormat("HH:mm"),
      startTime: slot.toISO() || "",
      available: true,
    }))
}

const matchesCapacity = (table: SidebarTable, peopleAmount: number) => {
  const minCapacity = table.minCapacity ?? DEFAULT_SIDEBAR_TABLE_MIN_CAPACITY
  const maxCapacity = table.maxCapacity ?? DEFAULT_SIDEBAR_TABLE_MAX_CAPACITY

  if (peopleAmount < minCapacity) return false
  if (peopleAmount > maxCapacity) return false

  return true
}

const hasTableAvailabilityAt = ({
  table,
  slot,
  busyBlocksByTable,
  openingRange,
  bookingThreshold,
  requiredDuration,
}: {
  table: SidebarTable
  slot: DateTime
  busyBlocksByTable: Record<string, Array<{ start: DateTime; end: DateTime }>>
  openingRange: SidebarOpeningRange
  bookingThreshold: DateTime
  requiredDuration: number
}) => {
  if (slot < bookingThreshold) return false

  const reservationEnd = slot.plus({ seconds: requiredDuration })
  if (reservationEnd > openingRange.closesAt) return false

  return !(busyBlocksByTable[table.id] || []).some((block) => block.start < reservationEnd && block.end > slot)
}

export const getSidebarAvailability = ({
  date,
  openingHours,
  tables = [],
  busyBlocks = [],
  timezone,
  minAdvanceBookingTime,
  minReservationTime,
  maxGuestCount = 12,
  selectedPeopleAmount,
}: SidebarAvailabilityInput) => {
  const openingRange = getSidebarOpeningRange(date, openingHours, timezone)

  if (!date || !openingRange) {
    return {
      isClosed: true,
      availableGuestCounts: [] as number[],
      timeSlots: [] as SidebarTimeSlot[],
    }
  }

  const bookingThreshold = minAdvanceBookingTime
    ? getRoundedBookingThreshold(DateTime.now().setZone(timezone).plus({ seconds: minAdvanceBookingTime }))
    : DateTime.now().setZone(timezone)
  const requiredDuration = getSidebarReservationDuration(minReservationTime)
  const slots = buildSidebarTimeSlots(openingRange)
  const busyBlocksByTable = busyBlocks.reduce<Record<string, Array<{ start: DateTime; end: DateTime }>>>(
    (acc, block) => {
      acc[block.tableId] ||= []
      acc[block.tableId].push({ start: DateTime.fromISO(block.startTime), end: DateTime.fromISO(block.endTime) })
      return acc
    },
    {},
  )

  const availableGuestCounts = Array.from({ length: maxGuestCount }, (_, index) => index + 1).filter((guestCount) => {
    const compatibleTables = tables.filter((table) => matchesCapacity(table, guestCount))

    if (compatibleTables.length === 0) return false

    return compatibleTables.some((table) =>
      slots.some((slot) =>
        hasTableAvailabilityAt({
          table,
          slot,
          busyBlocksByTable,
          openingRange,
          bookingThreshold,
          requiredDuration,
        }),
      ),
    )
  })

  const selectedTables = selectedPeopleAmount
    ? tables.filter((table) => matchesCapacity(table, selectedPeopleAmount))
    : []

  const timeSlots = slots.map((slot) => ({
    label: slot.toFormat("HH:mm"),
    startTime: slot.toISO() || "",
    available: selectedTables.some((table) =>
      hasTableAvailabilityAt({
        table,
        slot,
        busyBlocksByTable,
        openingRange,
        bookingThreshold,
        requiredDuration,
      }),
    ),
  }))

  return {
    isClosed: false,
    availableGuestCounts,
    timeSlots,
  }
}
